// Bank / credit-card reconciliation. Owner-only.
// Lists each connected institution's accounts, then for the chosen month shows
// every bank txn grouped by match status. Unmatched rows get three actions:
//   • Match…   — pick from same-amount, ±3-day candidates in the register
//   • Adopt    — create a new receipt/income from the bank txn
//   • Ignore   — bank fee, transfer, etc. — drop from the worklist

import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { api, fmtMoney, fmtDate } from '../lib/api.js';
import PlaidLink from '../components/PlaidLink.jsx';

const STATUS_LABELS = {
  unmatched:       'Unmatched (action needed)',
  auto_matched:    'Auto-matched',
  manual_matched:  'Manually matched',
  adopted:         'Adopted from bank',
  ignored:         'Ignored',
};

export default function Reconcile() {
  const { isOwner } = useOutletContext();
  const [status, setStatus] = useState(null);    // /api/plaid/status
  const [items, setItems] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [filter, setFilter] = useState('unmatched');
  const [syncing, setSyncing] = useState(false);
  const [err, setErr] = useState('');
  const [picker, setPicker] = useState(null);    // { bt, suggestions }

  if (!isOwner) {
    return <div className="card text-ink-700/80">Reconciliation is owner-only.</div>;
  }

  async function load() {
    setErr('');
    try {
      const s = await api.get('/api/plaid/status');
      setStatus(s);
      if (!s.configured) { setItems([]); setTransactions([]); setSummary(null); return; }
      const [it, list, sm] = await Promise.all([
        api.get('/api/plaid/items'),
        api.get(`/api/bank-transactions?status=${filter}&month=${month}`),
        api.get(`/api/bank-transactions/reconcile-summary?month=${month}`),
      ]);
      setItems(it.items || []);
      setTransactions(list.transactions || []);
      setSummary(sm);
    } catch (e) {
      setErr(e.message);
    }
  }
  useEffect(() => { load(); }, [filter, month]);

  async function syncNow() {
    setSyncing(true); setErr('');
    try {
      const r = await api.post('/api/plaid/sync', {});
      const errors = (r.summary || []).filter(s => s.error);
      if (errors.length) setErr(errors.map(e => `item ${e.item}: ${e.error}`).join(' · '));
      await load();
    } catch (e) { setErr(e.message); }
    finally { setSyncing(false); }
  }

  async function adopt(bt) {
    try { await api.post(`/api/bank-transactions/${bt.id}/adopt`, {}); load(); }
    catch (e) { setErr(e.message); }
  }
  async function ignore(bt) {
    try { await api.post(`/api/bank-transactions/${bt.id}/ignore`, {}); load(); }
    catch (e) { setErr(e.message); }
  }
  async function unmatch(bt) {
    try { await api.post(`/api/bank-transactions/${bt.id}/unmatch`, {}); load(); }
    catch (e) { setErr(e.message); }
  }
  async function openMatchPicker(bt) {
    try {
      const { suggestions } = await api.get(`/api/bank-transactions/${bt.id}/suggestions`);
      setPicker({ bt, suggestions });
    } catch (e) { setErr(e.message); }
  }
  async function applyMatch(bt, kind, target_id) {
    try {
      await api.post(`/api/bank-transactions/${bt.id}/match`, { kind, target_id });
      setPicker(null);
      load();
    } catch (e) { setErr(e.message); }
  }

  async function disconnect(item) {
    const accountSummary = (item.accounts || []).length
      ? ` (${item.accounts.length} account${item.accounts.length === 1 ? '' : 's'})`
      : '';
    if (!confirm(
      `Disconnect ${item.institution_name || 'this institution'}${accountSummary}?\n\n` +
      `This unlinks the bank from KingStead and revokes Plaid's access. ` +
      `Existing receipts/income that were matched to bank transactions stay; ` +
      `the bank-transaction records themselves are deleted along with the link. ` +
      `You'll need to re-connect to resume syncing.`,
    )) return;
    try {
      await api.delete(`/api/plaid/items/${item.id}`);
      load();
    } catch (e) { setErr(e.message); }
  }

  if (!status) return <div className="text-ink-700/60 italic">Loading…</div>;

  if (!status.configured) {
    return (
      <div className="card space-y-2">
        <h2 className="text-xl font-serif text-olive-900">Bank reconciliation</h2>
        <p className="text-ink-700/80">
          Plaid isn't configured on this server yet. Set <code>PLAID_CLIENT_ID</code> and <code>PLAID_SANDBOX_SECRET</code> as Fly secrets, then reload.
        </p>
        <p className="text-xs text-ink-700/60">
          Walkthrough: <code>docs/PLAID-SETUP.md</code>
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-serif text-olive-900">Reconcile</h1>
        <div className="flex items-center gap-2">
          <span className="pill bg-cream-100 ring-1 ring-olive-100 text-ink-700">env: {status.env}</span>
          <PlaidLink onSuccess={load} />
          <button className="btn-ghost" onClick={syncNow} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Sync now'}
          </button>
        </div>
      </header>

      {err && <div className="text-rust-700 text-sm">{err}</div>}

      {items.length === 0 ? (
        <div className="card text-ink-700/70 italic">
          No bank or credit-card accounts connected yet. Click <strong>Connect bank / card</strong> above.
          Sandbox login is <code>user_good</code> / <code>pass_good</code>.
        </div>
      ) : (
        <>
          <div className="card">
            <div className="label mb-2">Connected accounts</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {items.map(it => (
                <div key={it.id} className="border border-olive-100 rounded-lg p-3">
                  <div className="flex justify-between items-baseline">
                    <div className="font-medium">{it.institution_name || 'Bank'}</div>
                    <div className="text-xs text-ink-700/50">
                      {it.last_sync_at ? `synced ${fmtDate(it.last_sync_at)}` : 'never synced'}
                    </div>
                  </div>
                  {it.last_sync_error && (
                    <div className="text-xs text-rust-700 mt-1">⚠ {it.last_sync_error}</div>
                  )}
                  <ul className="text-sm mt-2 space-y-1">
                    {(it.accounts || []).map(a => (
                      <li key={a.id} className="flex justify-between">
                        <span>
                          {a.name}{a.mask ? ` ····${a.mask}` : ''}
                          <span className="text-xs text-ink-700/50 ml-1">({a.subtype || a.type})</span>
                        </span>
                        <span className="text-ink-700/70">
                          {a.current_balance_cents != null ? fmtMoney(a.current_balance_cents) : '—'}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex justify-end mt-2 pt-2 border-t border-olive-100/60">
                    <button onClick={() => disconnect(it)}
                            className="text-xs text-rust-700 hover:underline">
                      Disconnect
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="label">Month</label>
              <input className="input" type="month" value={month}
                     onChange={e => setMonth(e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Status</label>
              <select className="input" value={filter} onChange={e => setFilter(e.target.value)}>
                {Object.entries(STATUS_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            {summary && (
              <div className="flex flex-wrap gap-1 items-end">
                {summary.by_status.map(b => (
                  <span key={b.status} className="pill bg-cream-100 ring-1 ring-olive-100 text-ink-700"
                        title={`${b.n} txn · ${fmtMoney(b.total_cents)}`}>
                    {b.status}: {b.n}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-ink-700/60 border-b border-olive-100">
                  <th className="text-left py-2 px-2">Date</th>
                  <th className="text-left py-2 px-2">Account</th>
                  <th className="text-left py-2 px-2">Description</th>
                  <th className="text-right py-2 px-2">Amount</th>
                  <th className="text-right py-2 px-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-8 text-ink-700/60 italic">
                    Nothing in this view for {month}.
                  </td></tr>
                ) : transactions.map(t => (
                  <tr key={t.id} className="border-b border-olive-100/40">
                    <td className="py-1.5 px-2 whitespace-nowrap">
                      {fmtDate(t.date)}
                      {t.pending ? <span className="ml-1 pill bg-gold-500/15 text-gold-600">pending</span> : null}
                    </td>
                    <td className="py-1.5 px-2 text-xs text-ink-700/70">
                      {t.institution_name || ''} ····{t.account_mask || '—'}
                    </td>
                    <td className="py-1.5 px-2">
                      <div>{t.merchant_name || t.name}</div>
                      {t.category && <div className="text-xs text-ink-700/50">{t.category}</div>}
                    </td>
                    <td className={`py-1.5 px-2 text-right font-medium whitespace-nowrap ${t.amount_cents > 0 ? 'text-rust-700' : 'text-olive-900'}`}>
                      {t.amount_cents > 0 ? '−' : '+'}{fmtMoney(Math.abs(t.amount_cents))}
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      {filter === 'unmatched' ? (
                        <div className="flex justify-end gap-2">
                          <button className="btn-ghost text-xs" onClick={() => openMatchPicker(t)}>Match…</button>
                          <button className="btn-primary text-xs py-1 px-2" onClick={() => adopt(t)}>Adopt</button>
                          <button className="btn-ghost text-xs" onClick={() => ignore(t)}>Ignore</button>
                        </div>
                      ) : (
                        <button className="btn-ghost text-xs" onClick={() => unmatch(t)}>Unlink</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {picker && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-30 p-4"
             onClick={() => setPicker(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-serif text-lg text-olive-900 mb-1">
              Match {picker.bt.merchant_name || picker.bt.name}
            </h3>
            <p className="text-xs text-ink-700/60 mb-3">
              {fmtDate(picker.bt.date)} · {fmtMoney(Math.abs(picker.bt.amount_cents))} ·
              {picker.bt.amount_cents > 0 ? ' expense candidate' : ' income candidate'}
            </p>
            {picker.suggestions.length === 0 ? (
              <p className="text-ink-700/70 italic mb-3">
                No same-amount {picker.bt.amount_cents > 0 ? 'receipt' : 'income'} entries within ±3 days.
                Adopt creates a new one — or close this and enter the receipt manually first.
              </p>
            ) : (
              <ul className="space-y-2 mb-3">
                {picker.suggestions.map(s => (
                  <li key={`${s.kind}-${s.id}`}
                      className="flex justify-between items-center border border-olive-100 rounded p-2">
                    <div>
                      <div className="text-sm">{s.counterparty || '—'}</div>
                      <div className="text-xs text-ink-700/60">
                        {fmtDate(s.date)} · {s.kind} #{s.id} · {fmtMoney(s.amount_cents)}
                      </div>
                    </div>
                    <button className="btn-primary text-xs py-1 px-2"
                            onClick={() => applyMatch(picker.bt, s.kind, s.id)}>Link</button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex justify-end">
              <button className="btn-ghost" onClick={() => setPicker(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
